import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { TableSkeleton } from '@/components/tables/issue-table'
import { VerifyTable } from '@/components/tables/verify-table'
import { ViewHeader } from '@/components/shell/view-header'
import { useViewFilters } from '@/hooks/use-view-filters'
import { optionNames, useIssueFields, useIssues } from '@/lib/query'
import { ViewError } from '@/components/common/query-states'
import { hasQaGate, isLiveStage, isQaStage } from '@/lib/stage'
import { VERIFY_VIEW } from '@/lib/views'
import { liveReleasedAt, type WorkIssue } from '@/types/github'

// Two verification gates (architecture.md PR#7): QA is opt-in and fast,
// Prod is the SLA'd gate that closes issues. Legacy single-gate 'Released'
// issues count as Prod until that stage option drains and is deleted.
export default function Verify() {
  const { data: issues, isPending, isError, error, refetch } = useIssues()
  const fields = useIssueFields().data
  const chipFilter = useViewFilters()
  const [searchParams, setSearchParams] = useSearchParams()

  const twoGate = hasQaGate(optionNames(fields, 'Stage'))
  const gate = twoGate && searchParams.get('gate') === 'qa' ? 'qa' : 'prod'

  const { qa, prod } = useMemo(() => {
    const pool = chipFilter.apply(VERIFY_VIEW.pool(issues ?? [], { fields }))
    return {
      qa: pool.filter((i) => isQaStage(i.stage)),
      prod: pool.filter((i) => isLiveStage(i.stage)),
    }
  }, [issues, fields, chipFilter])

  const items = gate === 'qa' ? qa : prod

  const setGate = (g: 'qa' | 'prod') =>
    setSearchParams(
      (p) => {
        const next = new URLSearchParams(p)
        if (g === 'prod') next.delete('gate')
        else next.set('gate', g)
        return next
      },
      { replace: true },
    )

  const tabs = twoGate ? (
    <div className="border-border bg-surface ml-2 flex items-center gap-px rounded-lg border p-0.5">
      {(
        [
          ['qa', `QA (${qa.length})`],
          ['prod', `Prod (${prod.length})`],
        ] as const
      ).map(([g, label]) => (
        <Button
          key={g}
          size="sm"
          variant={gate === g ? 'secondary' : 'ghost'}
          className="h-6 px-2.5 text-xs"
          onClick={() => setGate(g)}
        >
          {label}
        </Button>
      ))}
    </div>
  ) : undefined

  const dateOf = useCallback(
    (i: WorkIssue) => (gate === 'qa' ? i.releasedToQaAt : liveReleasedAt(i)),
    [gate],
  )

  return (
    <div className="flex h-full flex-col">
      <ViewHeader
        title="Awaiting verification"
        meta={
          gate === 'qa'
            ? `${items.length} at the QA gate · oldest first · verify before it ships to prod — a QA pass carries through to auto-close on release`
            : `${items.length} live in Prod · oldest first · verify, then close the issue`
        }
        tabs={tabs}
      />
      {isError ? (
        <ViewError what="issues" error={error} onRetry={() => refetch()} />
      ) : isPending ? (
        <TableSkeleton />
      ) : (
        <VerifyTable
          issues={items}
          dateOf={dateOf}
          dateHeader={gate === 'qa' ? 'Released to QA' : 'Released to Prod'}
        />
      )}
    </div>
  )
}
